'use client';

import { Country, COUNTRY_KEY, Currency, CurrencyCountry, CURRENCY_KEY } from '../constants';
import { getCurrencyCookie } from '../utils';
import React, { useEffect, useState, type ReactNode } from 'react';
import { createSafeContext } from './createSafeContext';
import Cookies from 'js-cookie';
import { isClient } from '@skin-seoul/env-utils';

const DEFAULT_COUNTRY: Country = Country.Singapore;
const DEFAULT_CURRENCY: Currency = Currency.Singapore;

interface RegionCurrencyContextValue {
  country: Country;
  currency: Currency;
  setCountry: (country: Country) => void;
  setCurrency: (currency: Currency) => void;
}

const [RegionCurrencyProvider, useRegionCurrencyContext] =
  createSafeContext<RegionCurrencyContextValue>('RegionCurrencyContext');

interface RegionCurrencyProviderProps {
  children: ReactNode;
  initialCountry?: Country;
  initialCurrency?: Currency;
}

const setCountryCookie = (country: Country): void => {
  if (isClient()) {
    Cookies.set(COUNTRY_KEY, country);
  }
};

const setCurrencyCookie = (currency: Currency): void => {
  if (isClient()) {
    Cookies.set(CURRENCY_KEY, currency);
  }
};

export function RegionCurrencyContextProvider({ children }: RegionCurrencyProviderProps) {
  const initialCurrency = getCurrencyCookie();

  const getCountry = () => {
    if (initialCurrency) {
      return CurrencyCountry[initialCurrency];
    }
    return DEFAULT_COUNTRY;
  };

  // NOTE: We are using currency to set shipping country. so country and currency cannot be different.
  // But It can be different in future, so I seperated the functions.
  const initialCountry = getCountry();

  const [country, setCountryState] = useState<Country>(initialCountry);
  const [currency, setCurrencyState] = useState<Currency>(initialCurrency ?? DEFAULT_CURRENCY);

  const setCountry = (newCountry: Country) => {
    if (newCountry === country) {
      return;
    }
    setCountryState(newCountry);
    setCountryCookie(newCountry);
  };

  const setCurrency = (newCurrency: Currency) => {
    if (newCurrency === currency) {
      return;
    }
    setCurrencyState(newCurrency);
    setCurrencyCookie(newCurrency);
  };

  useEffect(() => {
    if (initialCurrency) {
      setCurrencyState(initialCurrency);
    }
    if (initialCountry) {
      setCountryState(initialCountry);
    }
  }, [initialCurrency, initialCountry, setCountryState, setCurrencyState]);

  return (
    <RegionCurrencyProvider
      value={{
        country,
        currency,
        setCountry,
        setCurrency,
      }}
    >
      {children}
    </RegionCurrencyProvider>
  );
}

export const useRegionCurrency = useRegionCurrencyContext;
